import React from 'react'
import { Link, router } from '@inertiajs/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { AppLayout } from '@/components/layouts/AppLayout'

interface BoardAgentData {
  id: number
  role: 'admin' | 'member'
  agent: {
    id: number
    username: string
    slug: string
    agent_type: 'human' | 'machine'
  }
  created_at: string
}

interface ProjectAgentData {
  agent: {
    id: number
    username: string
    slug: string
    agent_type: 'human' | 'machine'
  }
  project_role: string
}

type AccessRole = 'admin' | 'member' | 'no_access'

interface Props {
  board: { id: number; name: string; slug: string }
  board_agents: BoardAgentData[]
  project_agents: ProjectAgentData[]
}

export default function Settings({ board, board_agents, project_agents }: Props) {
  const boardAgentByAgentId: Record<number, BoardAgentData> = {}
  board_agents.forEach((ba) => {
    boardAgentByAgentId[ba.agent.id] = ba
  })

  function currentAccess(agentId: number): AccessRole {
    return (boardAgentByAgentId[agentId]?.role as AccessRole) || 'no_access'
  }

  function handleRoleChange(agentId: number, newRole: AccessRole) {
    const existing = boardAgentByAgentId[agentId]

    if (newRole === 'no_access') {
      if (existing) {
        router.delete(`/tasks/boards/${board.slug}/members/${existing.id}`, {
          preserveScroll: true,
        })
      }
      return
    }

    if (!existing) {
      router.post(
        `/tasks/boards/${board.slug}/members`,
        { board_agent: { agent_id: agentId, role: newRole } },
        { preserveScroll: true }
      )
      return
    }

    if (existing.role !== newRole) {
      router.put(
        `/tasks/boards/${board.slug}/members/${existing.id}`,
        { board_agent: { role: newRole } },
        { preserveScroll: true }
      )
    }
  }

  return (
    <AppLayout title={`${board.name} — Settings`}>
      <Box sx={{ maxWidth: 900 }}>
        <Box sx={{ mb: 2 }}>
          <Button component={Link} href="/tasks/all" size="small">
            Back to all tasks
          </Button>
          <Button component={Link} href={`/tasks/boards/${board.slug}/edit`} size="small" sx={{ ml: 1 }}>
            Edit board name
          </Button>
          <Button component={Link} href="/manage/cron_jobs" size="small" sx={{ ml: 1 }}>
            Cron Jobs
          </Button>
        </Box>

        <Typography variant="h2" sx={{ mb: 1 }}>
          Members
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose access for each project member. Selecting "No access" removes the member from this board.
        </Typography>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}
        >
          <Table size="small" sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell>Type</TableCell>
                <TableCell sx={{ width: 180 }}>Access</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {project_agents.map((pa) => (
                <TableRow key={pa.agent.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {pa.agent.username}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={pa.agent.agent_type}
                      size="small"
                      variant="outlined"
                      color={pa.agent.agent_type === 'machine' ? 'secondary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={currentAccess(pa.agent.id)}
                        onChange={(e) => handleRoleChange(pa.agent.id, e.target.value as AccessRole)}
                        size="small"
                        inputProps={{ 'data-testid': `access-${pa.agent.username}` }}
                      >
                        <MenuItem value="admin">Admin</MenuItem>
                        <MenuItem value="member">Member</MenuItem>
                        <MenuItem value="no_access">No access</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              ))}
              {project_agents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No project members.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </AppLayout>
  )
}
